import { cn } from '@helpers/cn';
import { useEffect, useMemo, useRef, useState } from 'react';
import { buildTokenDiff } from '../../../../../../helpers/diff';
import { splitTranscriptLines } from '../../../../../../helpers/transcript';
import { TRANSCRIPT_REVIEW_PROPOSAL_STATUS } from '../../../../../../constants';
import { compactSecondaryButtonClassName } from '../../../../../../styles';
import { DiffText } from '../DiffText';
import { TProps } from './types/TProps';

export const ProposalDiffView = ({ draftTranscript, proposal }: TProps) => {
  const changes = useMemo(() => proposal.patch.changes ?? [], [proposal.patch.changes]);
  const [activeChangeIndex, setActiveChangeIndex] = useState(0);
  const changeRefs = useRef<Record<number, HTMLDivElement | null>>({});
  const hasConflictView =
    proposal.status === TRANSCRIPT_REVIEW_PROPOSAL_STATUS.STALE || proposal.applyErrors.length > 0;

  const diffsByChange = useMemo(() => {
    const draftLines = splitTranscriptLines(draftTranscript);
    return changes.map((change) => {
      const currentText = draftLines[change.line - 1] ?? '';
      const expectedCurrentDiff = buildTokenDiff(change.expectedText, currentText);
      const proposedDiff = buildTokenDiff(hasConflictView ? currentText : change.expectedText, change.text);
      return { expectedCurrentDiff, proposedDiff };
    });
  }, [changes, draftTranscript, hasConflictView]);

  useEffect(() => {
    setActiveChangeIndex(0);
  }, [proposal.id]);

  useEffect(() => {
    const activeChange = changes[activeChangeIndex];
    if (!activeChange) {
      return;
    }

    changeRefs.current[activeChange.line]?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [activeChangeIndex, changes]);

  const selectChangeIndex = (nextIndex: number) => {
    setActiveChangeIndex(Math.min(Math.max(nextIndex, 0), changes.length - 1));
  };

  if (!changes.length) {
    return <div className="p-4 text-[var(--color-text-muted)]">No line changes in this proposal.</div>;
  }

  return (
    <div className="max-h-[720px] overflow-auto text-xs">
      <div className="sticky top-0 z-10 flex flex-wrap items-center justify-between gap-3 border-b border-[var(--color-border)] bg-[var(--color-surface)] p-3">
        <div className="flex flex-col gap-1">
          <div className="text-[var(--color-text-muted)]">
            Chunk {activeChangeIndex + 1} of {changes.length} · line {changes[activeChangeIndex]?.line}
          </div>
          {hasConflictView ? (
            <div className="text-[var(--color-error-heading)]">
              {proposal.applyErrors.length
                ? proposal.applyErrors.join('; ')
                : 'Proposal is stale against current draft.'}
            </div>
          ) : null}
        </div>
        <div className="flex gap-2">
          <button
            className={compactSecondaryButtonClassName}
            disabled={activeChangeIndex === 0}
            onClick={() => selectChangeIndex(activeChangeIndex - 1)}
          >
            Previous
          </button>
          <button
            className={compactSecondaryButtonClassName}
            disabled={activeChangeIndex >= changes.length - 1}
            onClick={() => selectChangeIndex(activeChangeIndex + 1)}
          >
            Next
          </button>
        </div>
      </div>
      <div
        className={cn(
          'hidden md:grid gap-0 bg-[var(--color-surface-muted)] border-b border-[var(--color-border)] font-semibold',
          hasConflictView
            ? 'grid-cols-1 md:grid-cols-[72px_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)]'
            : 'grid-cols-1 md:grid-cols-[72px_minmax(0,1fr)_minmax(0,1fr)]',
        )}
      >
        <div className="p-3 text-[var(--color-text-muted)]">Line</div>
        <div className="p-3 border-l border-[var(--color-border)]">{hasConflictView ? 'Expected' : 'Before'}</div>
        {hasConflictView ? <div className="p-3 border-l border-[var(--color-border)]">Current</div> : null}
        <div className="p-3 border-l border-[var(--color-border)]">{hasConflictView ? 'Proposed' : 'After'}</div>
      </div>
      {changes.map((change, changeIndex) => {
        const { expectedCurrentDiff, proposedDiff } = diffsByChange[changeIndex];
        const isActive = changeIndex === activeChangeIndex;
        const cellClassName = isActive ? 'bg-[var(--color-tint)]' : 'bg-[var(--color-surface)]';

        return (
          <div
            className={cn(
              'grid gap-0 border-b border-[var(--color-border)]',
              hasConflictView
                ? 'grid-cols-1 md:grid-cols-[72px_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)]'
                : 'grid-cols-1 md:grid-cols-[72px_minmax(0,1fr)_minmax(0,1fr)]',
            )}
            key={`${proposal.id}-diff-${change.line}`}
            ref={(element) => {
              changeRefs.current[change.line] = element;
            }}
          >
            <button
              className={cn(
                'p-3 text-left text-[var(--color-text-muted)] font-mono hover:bg-[var(--color-tint)]',
                cellClassName,
              )}
              onClick={() => selectChangeIndex(changeIndex)}
            >
              {change.line}
            </button>
            <div
              className={cn(
                'p-3 border-l border-[var(--color-border)] text-[var(--color-text)] whitespace-pre-wrap font-mono',
                cellClassName,
              )}
            >
              <span className="block font-semibold md:hidden">{hasConflictView ? 'Expected' : 'Before'}</span>
              <DiffText
                parts={hasConflictView ? expectedCurrentDiff.beforeParts : proposedDiff.beforeParts}
                side="before"
              />
            </div>
            {hasConflictView ? (
              <div
                className={cn(
                  'p-3 border-l border-[var(--color-border)] text-[var(--color-text)] whitespace-pre-wrap font-mono',
                  cellClassName,
                )}
              >
                <span className="block font-semibold md:hidden">Current</span>
                <DiffText parts={expectedCurrentDiff.afterParts} side="after" />
              </div>
            ) : null}
            <div
              className={cn(
                'p-3 border-l border-[var(--color-border)] text-[var(--color-text)] whitespace-pre-wrap font-mono',
                cellClassName,
              )}
            >
              <span className="block font-semibold md:hidden">{hasConflictView ? 'Proposed' : 'After'}</span>
              <DiffText parts={proposedDiff.afterParts} side="after" />
            </div>
          </div>
        );
      })}
    </div>
  );
};
